import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, X } from "lucide-react";
import { useTutorial } from '@/hooks/useTutorial';
import { 
  CashflowKingIcon, 
  VermogensBuildlerIcon, 
  SteuerNinjaIcon, 
  PassiveInvestorIcon 
} from '@/components/icons/InvestorIcons';

// Import actual icon images from assets
import cashflowKingIcon from '@/assets/icons/cashflow-king.png';
import vermogensBuildlerIcon from '@/assets/icons/vermogens-builder.png';
import steuerNinjaIcon from '@/assets/icons/steuer-ninja.png';
import passiveInvestorIcon from '@/assets/icons/passive-investor.png';

const investorIcons = {
  'Cashflow King': cashflowKingIcon,
  'Vermögens-Builder': vermogensBuildlerIcon,
  'Steuer-Ninja': steuerNinjaIcon,
  'Passive Investor': passiveInvestorIcon,
  'Ausgewogener Investor': vermogensBuildlerIcon
};

export default function TutorialOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showTutorial, tutorialStep, investorType, closeTutorial, nextTutorialStep } = useTutorial();
  const [spotlightPosition, setSpotlightPosition] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const investorIconSrc = investorType ? investorIcons[investorType as keyof typeof investorIcons] : vermogensBuildlerIcon;

  // Calculate spotlight position based on tutorial step
  useEffect(() => {
    if (!showTutorial) return;

    const updateSpotlight = () => {
      let element: HTMLElement | null = null;

      if (tutorialStep === 0) {
        // Dashboard: Portfolio button in sidebar
        element = document.querySelector('[data-portfolio-nav]') as HTMLElement;
      } else if (tutorialStep === 1) {
        // Portfolio: Add property button
        element = document.querySelector('[data-add-property-btn]') as HTMLElement;
      } else if (tutorialStep === 2) {
        // Property Dialog: Form submit button
        element = document.querySelector('[data-tutorial-submit-btn]') as HTMLElement;
      }

      console.log('Tutorial step:', tutorialStep, 'Element found:', !!element);

      if (element) {
        const rect = element.getBoundingClientRect();
        console.log('Element rect:', rect);
        setSpotlightPosition({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height
        });

        // Calculate tooltip position
        const content = getTutorialContent();
        if (content) {
          let tooltipX, tooltipY;
          
          if (content.tooltipPosition === "center") {
            // Center the tooltip on screen
            tooltipX = (window.innerWidth - 400) / 2;
            tooltipY = (window.innerHeight - 300) / 2;
          } else {
            tooltipX = tutorialStep === 0 
              ? rect.left + rect.width + content.tooltipOffset.x
              : rect.left - 400 + content.tooltipOffset.x;
            
            tooltipY = rect.top + content.tooltipOffset.y;
          }
          
          setTooltipPosition({
            x: Math.max(20, Math.min(tooltipX, window.innerWidth - 420)),
            y: Math.max(20, Math.min(tooltipY, window.innerHeight - 300))
          });
        }
      } else if (tutorialStep === 2) {
        // For step 3, center the tooltip on screen since there's no target element
        setTooltipPosition({
          x: (window.innerWidth - 400) / 2,
          y: (window.innerHeight - 300) / 2
        });
        setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 });
      } else {
        // Reset spotlight if element not found
        console.log('No element found, resetting spotlight');
        setSpotlightPosition({ x: 0, y: 0, width: 0, height: 0 });
      }
    };

    // Initial calculation
    updateSpotlight();
    
    // Add multiple delays to ensure DOM is updated, especially for route changes
    const timeoutId1 = setTimeout(updateSpotlight, 100);
    const timeoutId2 = setTimeout(updateSpotlight, 500);
    const timeoutId3 = setTimeout(updateSpotlight, 1000);

    // Recalculate on resize
    window.addEventListener('resize', updateSpotlight);
    return () => {
      window.removeEventListener('resize', updateSpotlight);
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [showTutorial, tutorialStep, location.pathname]);

  const handleCTAClick = (e?: React.MouseEvent) => {
    // Stop event propagation to prevent dialog from closing
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (tutorialStep === 0) {
      navigate('/portfoliopage');
      // Tutorial step will auto-advance via useTutorial hook
    } else if (tutorialStep === 1) {
      // Just advance to step 3 without opening the dialog
      nextTutorialStep();
    } else if (tutorialStep === 2) {
      // Just close the tutorial, don't close the dialog
      closeTutorial();
    }
  };

  const getTutorialContent = () => {
    switch (tutorialStep) {
      case 0:
        return {
          title: "Willkommen in Ihrem Dashboard!",
          description: "Lassen Sie uns Ihre erste Immobilie hinzufügen. Klicken Sie auf Portfolio, um zu beginnen.",
          cta: "Zu Portfolio gehen",
          tooltipPosition: "right",
          tooltipOffset: { x: 20, y: 0 }
        };
      case 1:
        return {
          title: "Perfekt! Jetzt fügen wir Ihre erste Immobilie hinzu.",
          description: "Als nächstes werden Sie das Formular zum Hinzufügen einer Immobilie sehen. Alle Informationen sind wichtig für eine präzise Analyse.",
          cta: "Weiter",
          tooltipPosition: "left",
          tooltipOffset: { x: -20, y: 0 }
        };
      case 2:
        return {
          title: "Immobilie hinzufügen - So geht's!",
          description: "Wenn Sie bereit sind, klicken Sie auf 'Immobilie hinzufügen' und füllen Sie alle Felder sorgfältig aus. Je vollständiger Ihre Angaben, desto präziser wird unsere Analyse. Am Ende des Formulars klicken Sie auf 'Hinzufügen', um die Immobilie zu Ihrem Portfolio hinzuzufügen.",
          cta: "Tutorial beenden",
          tooltipPosition: "center",
          tooltipOffset: { x: 0, y: 0 }
        };
      default:
        return null;
    }
  };

  const content = getTutorialContent();
  if (!showTutorial || !content) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999]"
        style={{ pointerEvents: 'none' }}
      >
        {/* Light background overlay - only for steps 1 and 2 */}
        {tutorialStep <= 2 && (
          <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        )}

        {/* Pulsing highlight around target element */}
        {spotlightPosition.width > 0 && spotlightPosition.height > 0 && (
          <motion.div
            className="absolute border-4 border-blue-500 rounded-xl pointer-events-none"
            animate={{
              left: spotlightPosition.x - 8,
              top: spotlightPosition.y - 8,
              width: spotlightPosition.width + 16,
              height: spotlightPosition.height + 16,
              boxShadow: [
                "0 0 0 0 rgba(59, 130, 246, 0.7)",
                "0 0 0 20px rgba(59, 130, 246, 0)",
                "0 0 0 0 rgba(59, 130, 246, 0)"
              ]
            }}
            transition={{
              left: { type: "spring", stiffness: 300, damping: 30, duration: 0.6 },
              top: { type: "spring", stiffness: 300, damping: 30, duration: 0.6 },
              width: { type: "spring", stiffness: 300, damping: 30, duration: 0.6 },
              height: { type: "spring", stiffness: 300, damping: 30, duration: 0.6 },
              boxShadow: { duration: 2, repeat: Infinity, ease: "easeOut" }
            }}
          />
        )}

        {/* Tutorial tooltip */}
        <motion.div
          className="absolute"
          style={{ pointerEvents: 'auto' }}
          animate={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            scale: 1,
            opacity: 1
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          transition={{
            scale: { delay: 0.3, duration: 0.4 },
            opacity: { delay: 0.3, duration: 0.4 },
            left: { type: "spring", stiffness: 200, damping: 25, duration: 0.8 },
            top: { type: "spring", stiffness: 200, damping: 25, duration: 0.8 }
          }}
        >
          <Card className="w-96 bg-white border-0 shadow-2xl">
            <CardContent className="p-6">
              {/* Close button */}
              <button
                onClick={closeTutorial}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-neutral-100 transition-colors"
              >
                <X className="h-4 w-4 text-neutral-600" />
              </button>

              {/* Progress indicator */}
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex space-x-1">
                  {[0, 1, 2].map((step) => (
                    <div
                      key={step}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        step <= tutorialStep ? 'bg-blue-600' : 'bg-neutral-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs text-neutral-600">
                  Schritt {tutorialStep + 1} von 3
                </span>
              </div>

              {/* Investor character */}
              <div className="flex justify-center mb-4">
                <img 
                  src={investorIconSrc} 
                  alt={investorType || 'Investor'} 
                  className="w-20 h-20 object-contain"
                />
              </div>

              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  {content.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {content.description}
                </p>
              </div>

              {/* Value preview for step 2 */}
              {tutorialStep === 1 && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
                  <div className="text-xs font-medium text-neutral-800 mb-2">
                    💡 Nach dem Hinzufügen erhalten Sie:
                  </div>
                  <ul className="text-xs text-neutral-700 space-y-1">
                    <li>• Automatische Bewertung nach Fahrländer-Methode</li>
                    <li>• Viadukt-Analyse für Optimierungspotenziale</li>
                    <li>• Personalisierte {investorType}-Empfehlungen</li>
                  </ul>
                </div>
              )}

              {/* CTA Button */}
              <Button
                onClick={(e) => handleCTAClick(e)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
              >
                {content.cta}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Arrow pointing to target */}
          {content.tooltipPosition !== "center" && (
            <div
              className={`absolute top-1/2 transform -translate-y-1/2 ${
                content.tooltipPosition === 'right' 
                  ? '-left-3' 
                  : '-right-3'
              }`}
            >
              <div
                className={`w-0 h-0 border-solid ${
                  content.tooltipPosition === 'right'
                    ? 'border-l-0 border-r-[12px] border-r-white border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent'
                    : 'border-r-0 border-l-[12px] border-l-white border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent'
                }`}
              />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}